/**
 * Staff Guide: Time Clock - Clock In/Out
 * Simple one-page printable PDF guide
 */
import { Printer, Clock, LogIn, LogOut, CheckCircle, AlertTriangle } from 'lucide-react';

export default function StaffTimeClockGuide() {
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      <div className="min-h-screen bg-white">
        {/* Header with Print Button */}
        <div className="no-print bg-blue-700 text-white py-4 px-6 flex justify-between items-center sticky top-0 z-50">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6" />
            <div>
              <h1 className="font-bold text-lg">Time Clock Guide</h1>
              <p className="text-blue-100 text-sm">Clock In / Clock Out</p>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-white text-blue-700 px-4 py-2 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print / Download PDF
          </button>
        </div>

        {/* Content */}
        <div className="max-w-2xl mx-auto p-8">
          {/* Title */}
          <div className="text-center mb-8 pb-6 border-b-2 border-blue-600">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Time Clock</h1>
            <p className="text-xl text-blue-700">Clock In / Clock Out</p>
            <p className="text-gray-500 mt-2">Bulaccino Cafe - Nadi, Fiji</p>
          </div>

          {/* Important Note */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-bold text-amber-800 text-lg mb-2">Before You Clock In</h2>
                <ul className="text-amber-700 space-y-1">
                  <li>You must be connected to <strong>&quot;BC-Namaka-S&quot;</strong> Wi-Fi</li>
                  <li>You must use your registered device (phone)</li>
                  <li>Always clock out when your shift ends</li>
                </ul>
              </div>
            </div>
          </div>

          {/* How to Clock IN */}
          <div className="mb-8 bg-green-50 border border-green-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                <LogIn className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-green-800">How to Clock IN</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <p className="font-semibold text-gray-800">Go to the Staff Portal</p>
                  <p className="text-gray-600">Open <strong>bulaccinocafe.com/staff</strong> on your phone</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <p className="font-semibold text-gray-800">Enter Your PIN</p>
                  <p className="text-gray-600">Type your 6-digit staff PIN</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <p className="font-semibold text-gray-800">Tap "Clock In"</p>
                  <p className="text-gray-600">The big green button on your dashboard</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <p className="font-semibold text-gray-800">Done!</p>
                  <p className="text-gray-600">You'll see "Currently Clocked In" with your start time</p>
                </div>
              </div>
            </div>
          </div>

          {/* How to Clock OUT */}
          <div className="mb-8 bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                <LogOut className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-red-800">How to Clock OUT</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <p className="font-semibold text-gray-800">Go to the Staff Portal</p>
                  <p className="text-gray-600">Open <strong>bulaccinocafe.com/staff</strong> on your phone</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <p className="font-semibold text-gray-800">Enter Your PIN</p>
                  <p className="text-gray-600">Type your 6-digit staff PIN</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <p className="font-semibold text-gray-800">Tap "Clock Out"</p>
                  <p className="text-gray-600">The red button on your dashboard</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <p className="font-semibold text-gray-800">Done!</p>
                  <p className="text-gray-600">You'll see your total hours worked for that shift</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
            <h2 className="font-bold text-gray-800 text-lg mb-4">Quick Tips</h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Clock in when you <strong>start</strong> your shift</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Clock out when you <strong>finish</strong> your shift</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Forgot to clock out? Tell your manager right away</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">View your hours in the "Time Clock History" section</span>
              </li>
            </ul>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center text-gray-500 text-sm">
            <p>Bulaccino Cafe - Nadi, Fiji</p>
            <p>Questions? Ask your manager or supervisor.</p>
          </div>
        </div>
      </div>
    </>
  );
}
